import { useMemo, useState } from 'react';
import { Car, Confetti, Door, DoorOpen, SignOut, Storefront, Warehouse, type Icon } from '@phosphor-icons/react';
import { InnenKarte, KartenTitel, SegmentedControl } from '../../../../../components/immobilien/ui';
import { heuteIso, qm } from '../../../../../lib/immobilien/format';
import { belegungJeMonat, deDatum, mieterName, type EinheitArt } from '../../../../../lib/immobilien/objektDetail';
import { letzteMonate } from '../../../../../lib/immobilien/portfolio';
import { useObjektSeite } from '../ObjektSeitenKontext';

const ART_ICON: Record<EinheitArt, Icon> = { wohnung: Door, gewerbe: Storefront, stellplatz: Car, sonstiges: Warehouse };
const MONAT = new Intl.DateTimeFormat('de-DE', { month: 'narrow', timeZone: 'UTC' });

type Filter = 'alle' | 'vermietet' | 'frei';

/** Panel „Belegung & Leerstand“. */
export function BelegungPanel() {
  const { uebersicht, kennzahlen: k, daten, zuEinheiten } = useObjektSeite();
  const [filter, setFilter] = useState<Filter>('alle');
  const heute = heuteIso();
  const monate = useMemo(() => letzteMonate(heute, 12), [heute]);
  const verlauf = useMemo(() => belegungJeMonat(daten.einheiten, daten.mieter, monate), [daten.einheiten, daten.mieter, monate]);
  const schnitt = verlauf.length ? Math.round(verlauf.reduce((s, v) => s + v, 0) / verlauf.length) : 0;

  // Geplante Auszüge: gekündigte Mieter mit Mietende in der Zukunft
  const auszuege = daten.mieter
    .filter(m => (m.status ?? '').toLowerCase().startsWith('gek') && m.mietende && m.mietende.slice(0, 10) >= heute)
    .map(m => ({ id: m.id, name: mieterName(m), einheit: uebersicht.find(e => e.id === m.einheit_id)?.bezeichnung ?? '–', bis: m.mietende?.slice(0, 10) ?? null, einheitId: m.einheit_id }));
  const frei = uebersicht.filter(e => !e.vermietet);

  const zahl = (art: EinheitArt) => uebersicht.filter(e => e.art === art).length;
  const umfang = 2 * Math.PI * 50;
  const sichtbar = uebersicht.filter(e => filter === 'alle' || (filter === 'vermietet' ? e.vermietet : !e.vermietet));

  return (
    <>
      <div className="flex flex-wrap" style={{ gap: 12 }}>
        <InnenKarte style={{ flex: '1 1 320px', flexDirection: 'row', alignItems: 'center', gap: 20 }}>
          <div className="relative shrink-0" style={{ width: 132, height: 132 }}>
            <svg width="132" height="132" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
              <circle cx="60" cy="60" r="50" fill="none" stroke="var(--fill2)" strokeWidth="14" />
              {k.belegungProzent > 0 && <circle cx="60" cy="60" r="50" fill="none" stroke="var(--ok)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(umfang * k.belegungProzent) / 100} ${umfang}`} />}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800 }}>{k.einheiten ? `${k.belegungProzent}%` : '–'}</span>
              <span style={{ fontSize: 11, color: 'var(--ink3)' }}>belegt</span>
            </div>
          </div>
          <div className="flex flex-col" style={{ gap: 4 }}>
            <span style={{ fontSize: 18, fontWeight: 800 }}>Belegungsquote</span>
            <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{k.vermietet} von {k.einheiten} Einheiten vermietet</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Ø letzte 12 Monate: {schnitt} %</span>
          </div>
        </InnenKarte>
        <div className="grid" style={{ flex: '1 1 300px', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 }}>
          {([['Vermietet', k.vermietet, Door, 'var(--ok)', 'var(--ok-soft)'], ['Frei', k.frei, DoorOpen, 'var(--warn)', 'var(--warn-soft)'],
            ['Gewerbe', zahl('gewerbe'), Storefront, 'var(--info)', 'var(--info-soft)'], ['Stellplätze', zahl('stellplatz'), Car, 'var(--acc)', 'var(--acc-soft)']] as const).map(([l, v, IconKomp, f, b]) => (
            <InnenKarte key={l} radius={20} padding={14} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: b }}><IconKomp weight="fill" size={19} color={f} /></span>
              <span className="flex flex-col"><span className="immo-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{v}</span><span style={{ fontSize: 12, color: 'var(--ink2)' }}>{l}</span></span>
            </InnenKarte>
          ))}
        </div>
      </div>

      <InnenKarte>
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>Einheiten-Übersicht</span>
          <SegmentedControl<Filter> segmente={[{ key: 'alle', label: 'Alle', anzahl: k.einheiten }, { key: 'vermietet', label: 'Vermietet', anzahl: k.vermietet }, { key: 'frei', label: 'Frei', anzahl: k.frei }]}
            wert={filter} onChange={setFilter} hoehe={30} ariaLabel="Filter" />
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
          {sichtbar.map(e => {
            const IconKomp = ART_ICON[e.art];
            return (
              <button key={e.id} type="button" onClick={() => zuEinheiten(e.id)} className="immo-hover-flaeche flex flex-col text-left cursor-pointer min-w-0"
                style={{ borderRadius: 16, padding: 12, gap: 8, border: 'none', background: 'var(--bg)', boxShadow: `inset 0 0 0 1px var(--line), inset 3px 0 0 ${e.vermietet ? 'var(--ok)' : 'var(--warn)'}`, color: 'var(--ink)' }}>
                <span className="flex items-center justify-between"><IconKomp weight="fill" size={16} color={e.vermietet ? 'var(--acc)' : 'var(--warn)'} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.vermietet ? 'var(--ok)' : 'var(--warn)' }} /></span>
                <span className="flex flex-col min-w-0">
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{e.bezeichnung}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.vermietet ? e.mieter.join(', ') : 'Leerstand'}</span>
                  <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink2)' }}>{e.flaeche > 0 ? qm(e.flaeche) : '–'}</span>
                </span>
              </button>
            );
          })}
        </div>
      </InnenKarte>

      <div className="flex flex-wrap" style={{ gap: 12 }}>
        <InnenKarte style={{ flex: '1.4 1 340px' }}>
          <KartenTitel>Verlauf 12 Monate</KartenTitel>
          <div className="flex items-end" style={{ gap: 6, height: 120 }}>
            {verlauf.map((v, i) => (
              <div key={monate[i]} title={`${monate[i]}: ${v} %`} className="flex flex-col items-center flex-1" style={{ gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{ width: '100%', maxWidth: 26, height: `${Math.max(4, v)}%`, borderRadius: 6, background: i === verlauf.length - 1 ? 'var(--acc)' : v < 100 ? 'var(--warn)' : 'var(--ok)', opacity: i === verlauf.length - 1 ? 1 : 0.75 }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink3)' }}>{MONAT.format(new Date(`${monate[i]}T00:00:00Z`))}</span>
              </div>
            ))}
          </div>
        </InnenKarte>
        <InnenKarte style={{ flex: '1 1 300px' }}>
          <KartenTitel>Leerstand & Auszüge</KartenTitel>
          {frei.length === 0 && auszuege.length === 0 && (
            <div className="flex items-center" style={{ gap: 10, fontSize: 13, color: 'var(--ink2)' }}>
              <Confetti weight="fill" size={20} color="var(--ok)" />Voll vermietet, keine Auszüge geplant.
            </div>
          )}
          {frei.map(e => (
            <button key={e.id} type="button" onClick={() => zuEinheiten(e.id)} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
              style={{ gap: 10, padding: 10, borderRadius: 14, border: 'none', background: 'var(--bg)', color: 'var(--ink)' }}>
              <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--warn-soft)' }}><DoorOpen weight="fill" size={16} color="var(--warn)" /></span>
              <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{e.bezeichnung}</span><span style={{ fontSize: 11, color: 'var(--ink3)' }}>Leerstand</span></span>
              <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{e.flaeche > 0 ? qm(e.flaeche) : ''}</span>
            </button>
          ))}
          {auszuege.map(a => (
            <button key={a.id} type="button" onClick={() => a.einheitId && zuEinheiten(a.einheitId)} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
              style={{ gap: 10, padding: 10, borderRadius: 14, border: 'none', background: 'var(--bg)', color: 'var(--ink)' }}>
              <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--info-soft)' }}><SignOut weight="fill" size={16} color="var(--info)" /></span>
              <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{a.einheit}</span><span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>Auszug {a.name}</span></span>
              <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--info)' }}>{deDatum(a.bis)}</span>
            </button>
          ))}
        </InnenKarte>
      </div>
    </>
  );
}
